import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import { DAYS_IN_YEAR } from '@/environment/SeasonModel';
import { el } from './dom';
import { formatDayOfYear } from './PlantMetrics';
import { seasonGradientCss, seasonMoment } from './ChronoPalette';
import { ChronoRibbon, paintDot } from './ChronoRibbon';

/**
 * **The calendar, borrowed.** A single date ribbon that can be dropped into any panel that draws
 * something the season changes — currently the header of the flora database, where the plate
 * thumbnails are painted from the plant's seasonal palette and are therefore a picture of a date.
 *
 * ## It is not a second calendar
 *
 * This is the point of the whole file, and it is the same rule the database sheet follows about
 * every figure it draws: there is no second copy of anything. The scrubber holds no date of its
 * own. It reads {@link EnvironmentManager} on every refresh and writes straight back into it, which
 * means:
 *
 * - Dragging it here moves the sun, the foliage, the ground cover and the shadows in the viewport,
 *   because that is what writing a date to the environment does.
 * - The floating {@link ChronoBar} over the scene follows on the next refresh, because it reads the
 *   same field — as does the date shown anywhere else in the HUD.
 * - Moving the date from the bar, the tray, or a loaded project moves *this* handle, for the same
 *   reason and by the same path.
 *
 * There is no synchronisation code anywhere in here, because there are never two values to
 * reconcile. The ribbon itself is {@link ChronoRibbon} — literally the widget the floating bar is
 * built from, not a small copy of it.
 *
 * What it deliberately does *not* carry is time of day. The scrubber exists to answer "what does
 * this look like in May", and an hour hand beside it would be a control for a question nobody asks
 * of a wall of 2D cards; the floating bar remains the place where the light is set.
 */

export interface DateScrubberDeps {
  environment: EnvironmentManager;
  /**
   * The date moved. The host repaints whatever it draws from the season, and — being the one that
   * knows about the rest of the app — passes the change on to the scene and the rest of the HUD.
   */
  onChange: () => void;
  /** Accessible name for the handle. Defaults to "Scene date". */
  label?: string;
  /** Short word above the ribbon. Defaults to "Date". */
  title?: string;
}

/**
 * One wheel notch over a date ribbon, wherever one appears. Coarse enough to cross a season, fine
 * enough to land in one — and shared with the floating bar so the two feel like the same control.
 */
export const WHEEL_DAYS = 3;

/**
 * Write a day of the year to the scene.
 *
 * The one rule every date control in the app shares: dragging a calendar is a statement that the
 * calendar is what matters, and the four-season shortcut is a coarser view of the same axis — so
 * adopt the fine one rather than silently ignoring the drag or leaving the two disagreeing.
 */
export function setSceneDate(environment: EnvironmentManager, dayOfYear: number): void {
  environment.setSeasonMode('calendar');
  environment.setDayOfYear(dayOfYear);
}

export class DateScrubber {
  readonly root: HTMLElement;

  private readonly ribbon: ChronoRibbon;
  private readonly dot: HTMLElement;
  private readonly value: HTMLElement;
  private readonly caption: HTMLElement;

  constructor(private readonly deps: DateScrubberDeps) {
    this.dot = el('span', { class: 'lg-chrono__dot lg-datescrub__dot' });
    this.value = el('span', { class: 'lg-datescrub__value' });
    this.caption = el('span', { class: 'lg-datescrub__caption' });

    this.ribbon = new ChronoRibbon({
      kind: 'date',
      label: deps.label ?? 'Scene date',
      min: 1,
      max: DAYS_IN_YEAR,
      step: 1,
      wheel: WHEEL_DAYS,
      glyph: 12,
      onInput: (day) => {
        setSceneDate(deps.environment, day);
        // Repaint the readout now rather than waiting for the host: the digits sit directly above
        // the handle being dragged and are the thing being read while dragging it.
        this.refresh();
        deps.onChange();
      },
    });
    this.ribbon.track.style.backgroundImage = seasonGradientCss();

    this.root = el(
      'div',
      { class: 'lg-datescrub' },
      el(
        'div',
        { class: 'lg-datescrub__read' },
        this.dot,
        el('span', { class: 'lg-datescrub__title', text: deps.title ?? 'Date' }),
        this.value,
        this.caption,
      ),
      this.ribbon.root,
    );

    this.refresh();
  }

  /** Re-read the scene's date and repaint. Safe to call every frame: no DOM is rebuilt. */
  refresh(): void {
    const day = this.deps.environment.effectiveDayOfYear();
    const moment = seasonMoment(day);
    const label = formatDayOfYear(day);
    this.ribbon.set(day, moment, label);
    paintDot(this.dot, moment, 11);
    this.value.textContent = label;
    this.caption.textContent = moment.label;
  }

  dispose(): void {
    this.ribbon.dispose();
    this.root.remove();
  }
}
